import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type {
  AgentPermissionMode,
  Artifact,
  ClientCommand,
  ContextUsage,
  Draft,
  EffortLevel,
  Participant,
  PermissionRequest,
  RoomFile,
  RoomStatus,
  RoomView,
  RoomVisibility,
  ServerEvent,
  SlashCommandInfo,
  TranscriptEntry,
  UserProfile,
} from '@shared/protocol';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface CoworkState {
  connection: ConnectionState;
  me?: UserProfile;
  room?: RoomView;
  rooms: RoomView[];
  participants: Participant[];
  entries: TranscriptEntry[];
  truncated: boolean;
  status: RoomStatus;
  queueDepth: number;
  totalCostUsd: number;
  permission?: PermissionRequest;
  files: RoomFile[];
  /** What Claude published, newest first. */
  artifacts: Artifact[];
  /** Other people's unsent messages, by connection id. */
  drafts: Draft[];
  /** Models a room owner may pick from; the server default first. */
  models: string[];
  /** How full the context window was after the last turn. */
  context?: ContextUsage;
  /** What the composer offers after "/". */
  commands: SlashCommandInfo[];
  error?: string;
}

const initialState: CoworkState = {
  connection: 'connecting',
  rooms: [],
  participants: [],
  entries: [],
  truncated: false,
  status: 'idle',
  queueDepth: 0,
  totalCostUsd: 0,
  files: [],
  artifacts: [],
  drafts: [],
  models: [],
  commands: [],
};

type Action =
  | { type: 'connection'; state: ConnectionState }
  | { type: 'event'; event: ServerEvent }
  /** Coalesced text deltas, flushed once per animation frame. */
  | { type: 'deltas'; deltas: Map<string, string> }
  | { type: 'dismiss-error' }
  | { type: 'error'; message: string };

/**
 * Finds an entry by id, scanning from the end. Streaming deltas almost always
 * target the newest entry, so this is O(1) in practice on long transcripts.
 */
function indexOfEntry(entries: TranscriptEntry[], id: string): number {
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i]!.id === id) return i;
  }
  return -1;
}

function patchEntry(
  entries: TranscriptEntry[],
  id: string,
  patch: (entry: TranscriptEntry) => TranscriptEntry,
): TranscriptEntry[] {
  const i = indexOfEntry(entries, id);
  if (i === -1) return entries;
  const next = entries.slice();
  next[i] = patch(entries[i]!);
  return next;
}

function reduce(state: CoworkState, action: Action): CoworkState {
  switch (action.type) {
    case 'connection':
      return { ...state, connection: action.state };

    case 'dismiss-error':
      return { ...state, error: undefined };

    case 'error':
      return { ...state, error: action.message };

    case 'deltas': {
      let entries = state.entries;
      for (const [id, text] of action.deltas) {
        entries = patchEntry(entries, id, (e) => ({ ...e, text: (e.text ?? '') + text }));
      }
      return entries === state.entries ? state : { ...state, entries };
    }

    case 'event': {
      const ev = action.event;
      switch (ev.t) {
        case 'hello':
          return {
            ...state,
            connection: 'open',
            me: ev.me,
            room: ev.room,
            rooms: ev.rooms,
            participants: ev.participants,
            entries: ev.entries,
            truncated: ev.truncated,
            status: ev.room.status,
            totalCostUsd: ev.room.totalCostUsd,
            permission: ev.pendingPermission,
            files: ev.files ?? [],
            artifacts: ev.artifacts ?? [],
            drafts: ev.drafts ?? [],
            models: ev.models ?? [],
            context: ev.context,
            commands: ev.commands ?? [],
            queueDepth: 0,
            error: undefined,
          };

        case 'entry':
          return { ...state, entries: [...state.entries, ev.entry] };

        case 'delta':
          return {
            ...state,
            entries: patchEntry(state.entries, ev.entryId, (e) => ({
              ...e,
              text: (e.text ?? '') + ev.text,
            })),
          };

        case 'entry-done':
          return {
            ...state,
            entries: patchEntry(state.entries, ev.entryId, (e) => ({
              ...e,
              streaming: false,
              text: ev.text !== undefined ? ev.text : e.text,
            })),
          };

        case 'tool-result':
          return {
            ...state,
            entries: patchEntry(state.entries, ev.entryId, (e) =>
              e.tool ? { ...e, tool: { ...e.tool, result: ev.result } } : e,
            ),
          };

        case 'status':
          return {
            ...state,
            status: ev.status,
            room: state.room
              ? {
                  ...state.room,
                  status: ev.status,
                  model: ev.model ?? state.room.model,
                  effort: ev.effort ?? state.room.effort,
                  permissionMode: ev.permissionMode ?? state.room.permissionMode,
                  sessionId: ev.sessionId ?? state.room.sessionId,
                  authSource: ev.authSource ?? state.room.authSource,
                }
              : state.room,
          };

        case 'participants':
          return { ...state, participants: ev.participants };

        case 'rooms':
          return { ...state, rooms: ev.rooms };

        case 'queue':
          return { ...state, queueDepth: ev.depth };

        case 'permission-request':
          return { ...state, permission: ev.request };

        case 'permission-resolved':
          return state.permission?.id === ev.outcome.id
            ? { ...state, permission: undefined }
            : state;

        case 'cost':
          return { ...state, totalCostUsd: ev.totalCostUsd };

        case 'files':
          return { ...state, files: ev.files };

        case 'context':
          return { ...state, context: ev.context };

        case 'commands':
          return { ...state, commands: ev.commands };

        case 'artifacts':
          return { ...state, artifacts: ev.artifacts };

        case 'draft': {
          const others = state.drafts.filter((d) => d.id !== ev.draft.id);
          const i = state.drafts.findIndex((d) => d.id === ev.draft.id);
          if (!ev.draft.text) return i === -1 ? state : { ...state, drafts: others };
          // Keep the slot of an existing draft so bubbles do not reshuffle
          // every time someone else types a letter.
          const drafts = i === -1 ? [...others, ev.draft] : state.drafts.map((d, j) => (j === i ? ev.draft : d));
          return { ...state, drafts };
        }

        case 'error':
          return { ...state, error: ev.message };

        case 'pong':
          return state;

        default:
          return state;
      }
    }

    default:
      return state;
  }
}

/** How often a draft is sent while typing. Fast enough to read as live. */
const DRAFT_INTERVAL_MS = 120;

/** Reconnect backoff: 0.5s, 1s, 2s, 4s, 8s, then every 15s, with jitter. */
function backoffMs(attempt: number): number {
  const base = Math.min(500 * 2 ** attempt, 15_000);
  return base + Math.random() * 400;
}

export function useCowork(roomId?: string) {
  const [state, dispatch] = useReducer(reduce, initialState);

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const retryRef = useRef<number | undefined>(undefined);
  const closedRef = useRef(false);

  // Deltas are coalesced per frame: a fast stream can emit hundreds of tiny
  // messages a second, and one state update per frame keeps typing smooth.
  const pendingDeltas = useRef(new Map<string, string>());
  const frameRef = useRef<number | undefined>(undefined);

  const flushDeltas = useCallback(() => {
    frameRef.current = undefined;
    if (pendingDeltas.current.size === 0) return;
    const deltas = pendingDeltas.current;
    pendingDeltas.current = new Map();
    dispatch({ type: 'deltas', deltas });
  }, []);

  const queueDelta = useCallback(
    (entryId: string, text: string) => {
      pendingDeltas.current.set(entryId, (pendingDeltas.current.get(entryId) ?? '') + text);
      frameRef.current ??= requestAnimationFrame(flushDeltas);
    },
    [flushDeltas],
  );

  const connect = useCallback(() => {
    if (closedRef.current) return;

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const qs = roomId ? `?room=${encodeURIComponent(roomId)}` : '';
    const socket = new WebSocket(`${proto}//${location.host}/ws${qs}`);
    socketRef.current = socket;

    dispatch({ type: 'connection', state: attemptRef.current === 0 ? 'connecting' : 'reconnecting' });

    socket.onopen = () => {
      attemptRef.current = 0;
    };

    socket.onmessage = (raw) => {
      let event: ServerEvent;
      try {
        event = JSON.parse(raw.data as string) as ServerEvent;
      } catch {
        return;
      }
      // Deltas take the batched path; everything else is applied immediately
      // so ordering against the deltas around it is preserved by the flush.
      if (event.t === 'delta') {
        queueDelta(event.entryId, event.text);
        return;
      }
      flushDeltas();
      dispatch({ type: 'event', event });
    };

    socket.onclose = (e) => {
      socketRef.current = null;
      if (closedRef.current) return;

      // 1008/4001 mean the server rejected us; a reload sends the user to login.
      if (e.code === 1008 || e.code === 4001) {
        dispatch({ type: 'connection', state: 'closed' });
        location.reload();
        return;
      }

      dispatch({ type: 'connection', state: 'reconnecting' });
      const delay = backoffMs(attemptRef.current++);
      retryRef.current = window.setTimeout(connect, delay);
    };

    socket.onerror = () => {
      // onclose always follows; the retry is scheduled there.
    };
  }, [roomId, queueDelta, flushDeltas]);

  useEffect(() => {
    closedRef.current = false;
    connect();

    return () => {
      closedRef.current = true;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [connect]);

  // A tab that comes back from sleep often has a socket the OS already killed
  // without firing onclose. Nudge it so the user is not stuck on a dead view.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const s = socketRef.current;
      if (!s || s.readyState === WebSocket.CLOSED || s.readyState === WebSocket.CLOSING) {
        if (retryRef.current) clearTimeout(retryRef.current);
        attemptRef.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [connect]);

  const send = useCallback((cmd: ClientCommand) => {
    const s = socketRef.current;
    if (s?.readyState === WebSocket.OPEN) s.send(JSON.stringify(cmd));
  }, []);

  // Drafts are throttled, not debounced: a debounce would show nothing until
  // the typist paused, which is exactly the "live" part this exists for.
  const draftTimer = useRef<number | undefined>(undefined);
  const draftPending = useRef<string | null>(null);

  const cancelDraft = useCallback(() => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = undefined;
    draftPending.current = null;
  }, []);

  const draft = useCallback(
    (text: string) => {
      if (!text.trim()) {
        // Clearing is sent at once, so an emptied box empties everywhere.
        cancelDraft();
        send({ t: 'draft', text: '' });
        return;
      }
      draftPending.current = text;
      if (draftTimer.current) return;
      send({ t: 'draft', text });
      draftPending.current = null;
      const flush = () => {
        draftTimer.current = undefined;
        if (draftPending.current === null) return;
        send({ t: 'draft', text: draftPending.current });
        draftPending.current = null;
        draftTimer.current = window.setTimeout(flush, DRAFT_INTERVAL_MS);
      };
      draftTimer.current = window.setTimeout(flush, DRAFT_INTERVAL_MS);
    },
    [send, cancelDraft],
  );

  useEffect(() => cancelDraft, [cancelDraft]);

  const actions = useMemo(
    () => ({
      prompt: (text: string) => {
        // A throttled draft still in flight would land after the prompt and
        // resurrect the bubble. The server clears the draft on prompt itself.
        cancelDraft();
        send({ t: 'prompt', text });
      },
      draft,
      interrupt: () => send({ t: 'interrupt' }),
      typing: (active: boolean) => send({ t: 'typing', active }),
      decide: (id: string, decision: 'allow' | 'deny') =>
        send({ t: 'permission-decide', id, decision }),
      switchRoom: (id: string) => send({ t: 'switch-room', roomId: id }),
      createRoom: (name: string) => send({ t: 'create-room', name }),
      deleteRoom: (roomId: string) => send({ t: 'delete-room', roomId }),
      shareRoom: (roomId: string, email: string) => send({ t: 'share-room', roomId, email }),
      unshareRoom: (roomId: string, email: string) => send({ t: 'unshare-room', roomId, email }),
      setRoomVisibility: (roomId: string, visibility: RoomVisibility) =>
        send({ t: 'set-room-visibility', roomId, visibility }),
      setMode: (mode: AgentPermissionMode) =>
        send({ t: 'set-permission-mode', mode }),
      setModel: (model: string) => send({ t: 'set-model', model }),
      setEffort: (effort: EffortLevel) => send({ t: 'set-effort', effort }),
      dismissError: () => dispatch({ type: 'dismiss-error' }),
      /** For failures that happen outside the socket, such as an upload. */
      showError: (message: string) => dispatch({ type: 'error', message }),
    }),
    [send, draft, cancelDraft],
  );

  return { state, actions };
}
